import { useEffect } from 'react'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { clikArtService } from '@/api/services/clikart.service'
import { queryKeys } from '@/api/query-keys'

const IDLE_TIMEOUT_MS = 5000
const FALLBACK_DELAY_MS = 2000

/** Whether this login has already asked the backend to scan. A remount (or a second tab's editor)
 *  must not ask again; logout resets it so the next login does. */
let scanRequested = false

/** Counts logouts. A scan request remembers the value it started under; when its reply arrives
 *  after a logout the value has moved on, and the reply belongs to the previous book. */
let logoutCount = 0

/**
 * Called at logout: the next login asks for a scan of its own book, and the placed ids cached
 * from this login's book are dropped so a different book never briefly shows them. A request
 * still in flight at this point is ignored when it replies (see `logoutCount`).
 */
export function resetClikArtPlacedIdsScan(queryClient: QueryClient): void {
  scanRequested = false
  logoutCount += 1
  queryClient.removeQueries({ queryKey: queryKeys.clikArt.placedIds() })
}

/**
 * Asks the backend to scan the book's spreads for placed ClikArt, once per login. The request
 * returns right away — the backend scans in the background and only starts one when the last scan
 * is more than an hour old — and its reply is written into the placed-ids query, which then
 * polls while the scan runs. Runs when the browser is idle so it never competes with the spread
 * load, and a failure is silent: the editor just keeps the ids it already has.
 */
export function useClikArtPlacedIdsScan(enabled: boolean) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!enabled || scanRequested) return undefined

    const scan = () => {
      if (scanRequested) return

      scanRequested = true

      const startedAtLogoutCount = logoutCount
      const isFromPreviousLogin = () => logoutCount !== startedAtLogoutCount

      clikArtService
        .scanPlacedClikArtIds()
        .then((placedIds) => {
          // The user logged out while this was in flight: these ids are the old book's, and
          // writing them would show them as the next login's.
          if (isFromPreviousLogin()) return

          queryClient.setQueryData(queryKeys.clikArt.placedIds(), placedIds)
        })
        .catch(() => {
          // Silent by design (see above). Allow a later mount to try again — but not on behalf
          // of a previous login's request, which would re-open the next login's own guard.
          if (isFromPreviousLogin()) return

          scanRequested = false
        })
    }

    if (typeof window.requestIdleCallback === 'function') {
      const idleId = window.requestIdleCallback(scan, { timeout: IDLE_TIMEOUT_MS })

      return () => window.cancelIdleCallback(idleId)
    }

    const timeoutId = window.setTimeout(scan, FALLBACK_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [enabled, queryClient])
}
